import React from 'react';
import {
  AssessmentTestContext,
  AssessmentTestContextType,
  SyncStatus,
} from '../../contexts/assessment-test';
import { useMutation } from 'react-query';
import { syncAssessmentTestSession } from '../../api/teams';
import useAuth from '../use-auth';
import debounce from 'lodash.debounce';
import { AssessmentTestStepAnswer } from '../../api/admin';
import { Prompt } from 'react-router-dom';

const IDLE_SYNC_INTERVAL_MS = 30000;

export function useAutosave() {
  const [state] = React.useContext(AssessmentTestContext);
  const { syncMutation } = useSyncMutation();

  const debouncedSync = React.useMemo(
    () => debounce(syncMutation.mutate, 3000),
    [syncMutation.mutate]
  );

  React.useEffect(() => {
    if (state.sync.status !== SyncStatus.Queued) {
      return;
    }

    const BACKOFF_INITIAL_MS = 2000;
    const BACKOFF_MAX_MS = 60 * 1000; // 1 minute
    const attempt = state.sync.backoffAttempt ?? 0;

    // attempt 0 or 1 => immediate retry; 2+ => exponential delay capped at BACKOFF_MAX_MS
    if (attempt <= 1) {
      // The sync that carries finishedAt must not sit in a cancellable
      // debounce: the outro invites the learner to leave straight away, and
      // unmounting cancels the debounce — the finish would never reach the
      // server and the session would stay "in progress" on the home page.
      if (state.finishedAt) {
        syncMutation.mutate(state);
      } else {
        debouncedSync(state);
      }
      return;
    }

    const delay = Math.min(
      BACKOFF_INITIAL_MS * Math.pow(2, attempt - 1),
      BACKOFF_MAX_MS
    );

    const timer = setTimeout(() => {
      syncMutation.mutate(state);
    }, delay);

    return () => clearTimeout(timer);
  }, [
    state.sync.status,
    state.sync.backoffAttempt,
    debouncedSync,
    syncMutation,
    state,
  ]);

  React.useEffect(() => {
    const interval = setInterval(() => {
      const timeSinceLastSync =
        Date.now() - (state.sync.lastSyncedAt?.getTime() ?? 0);

      // Once the finished state has reached the server there is nothing new to
      // save — without this, a tab left open on the outro re-sends the same
      // finished payload every interval indefinitely.
      const isFinishedAndSynced =
        state.finishedAt !== null &&
        state.sync.lastSyncedAt !== null &&
        state.sync.lastSyncedAt.getTime() > state.finishedAt.getTime();

      const shouldSyncWhileIdle =
        !isFinishedAndSynced &&
        state.sync.status === SyncStatus.Idle &&
        timeSinceLastSync >= IDLE_SYNC_INTERVAL_MS;

      if (shouldSyncWhileIdle) {
        syncMutation.mutate(state);
      }
    }, IDLE_SYNC_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [state.sync.status, state.sync.lastSyncedAt, syncMutation, state]);

  React.useEffect(() => {
    return () => {
      debouncedSync.cancel();
    };
  }, [debouncedSync]);

  return { syncMutation };
}

export function useSyncMutation() {
  const [, dispatch] = React.useContext(AssessmentTestContext);
  const { getTokenSilently } = useAuth();

  const syncMutation = useMutation({
    mutationFn: async (state: AssessmentTestContextType) => {
      const token = await getTokenSilently();
      return syncAssessmentTestSession(token, {
        companyId: state.companyId,
        testId: state.testId,
        testSessionId: state.testSessionId,
        data: {
          startedAt: state.startedAt ?? new Date(),
          finishedAt: state.finishedAt,
          finishReason: state.finishReason,
          answers: parseAssessmentTestStepAnswers(state.steps),
        },
      });
    },
    onMutate: () => {
      dispatch({ type: 'SYNC_START' });
    },
    onSuccess: () => {
      dispatch({ type: 'SYNC_SUCCESS', timestamp: new Date() });
    },
    onError: (error: Error) => {
      dispatch({
        type: 'SYNC_ERROR',
        error: error.message || 'Sync failed',
      });
    },
  });
  return {
    syncMutation,
  };
}

export function ExitGuard() {
  const [state] = React.useContext(AssessmentTestContext);

  const hasUnsavedChanges = React.useMemo(() => {
    return (
      state.sync.status !== SyncStatus.Idle || state.sync.lastSyncedAt === null
    );
  }, [state.sync.status, state.sync.lastSyncedAt]);

  const isInAssessment = state.screen.type === 'STEP';

  const handleNavigationAttempt = React.useCallback(() => {
    return 'You have not finished the assessment. Are you sure you want to leave?';
  }, []);

  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () =>
        window.removeEventListener('beforeunload', handleBeforeUnload);
    }
  }, [hasUnsavedChanges]);

  return (
    <>
      {isInAssessment && (
        <Prompt when={hasUnsavedChanges} message={handleNavigationAttempt} />
      )}
    </>
  );
}

function parseAssessmentTestStepAnswers(
  steps: AssessmentTestContextType['steps']
): AssessmentTestStepAnswer[] {
  const now = new Date();

  return steps.map((step) => ({
    id: step.id,
    introduction: step.introduction
      ? {
          timeIntervals: closeOpenIntervals(
            step.introduction.timeIntervals,
            now
          ),
        }
      : null,
    sections: step.sections.map((section) =>
      mapSectionToAnswerFormat(section, now)
    ),
  }));
}

function closeOpenIntervals(
  timeIntervals: Array<{ startedAt: Date; endedAt: Date | null }>,
  timestamp: Date
) {
  return timeIntervals.map((interval) => ({
    ...interval,
    endedAt: interval.endedAt ?? timestamp,
  }));
}

function mapSectionToAnswerFormat(
  section: AssessmentTestContextType['steps'][number]['sections'][number],
  timestamp: Date
): AssessmentTestStepAnswer['sections'][number] {
  const base = {
    id: section.id,
    didInteract: section.didInteract,
    isAnswered: section.isAnswered,
    timeIntervals: closeOpenIntervals(section.timeIntervals, timestamp),
    followUpAnswer: section.followUpAnswer ?? null,
  };

  switch (section.type) {
    case 'text-input':
      return { ...base, type: 'text-input', answer: section.answer };
    case 'ranking':
      return { ...base, type: 'ranking', answer: section.answer };
    case 'multiple-choice':
      return { ...base, type: 'multiple-choice', answer: section.answer };
    case 'matching':
      return { ...base, type: 'matching', answer: section.answer };
    case 'fill-in-the-gap':
      return { ...base, type: 'fill-in-the-gap', answer: section.answer };
    case 'text-template':
      return { ...base, type: 'text-template', answer: section.answer };
    case 'py-playground':
      return { ...base, type: 'py-playground', answer: section.answer };
    case 'llm-playground':
      return { ...base, type: 'llm-playground', answer: section.answer };
    case 'multiple-rating':
      return { ...base, type: 'multiple-rating', answer: section.answer };
    case 'py-editor':
      return { ...base, type: 'py-editor', answer: section.answer };
    case 'llm-editor':
      return { ...base, type: 'llm-editor', answer: section.answer };
    case 'matrix-ranking':
      return { ...base, type: 'matrix-ranking', answer: section.answer };
    default: {
      throw new Error(`Unhandled section type`);
    }
  }
}
